import styled from 'styled-components';
import { styled as muiStyled } from '@mui/material/styles';
import { 
  TextField, 
  Button, 
  Select, 
  TableCell, 
  TableRow, 
  Paper, 
  Table, 
  Card, 
  Typography,
  TableContainer,
  Box
} from '@mui/material';
import theme from '../../config/theme';

export const InputField = muiStyled(TextField)(({ theme }) => ({
  width: '100%',
  boxSizing: 'border-box',
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.shape.borderRadius || 12,
    height: 56,
    width: '100%',
    boxSizing: 'border-box',
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#ffffff',
  },
  '& .MuiOutlinedInput-input': {
    padding: '0 14px',
    boxSizing: 'border-box',
  },
  '& .MuiInputLabel-root': {
    transform: 'translate(14px, 16px) scale(1)',
  },
  '& .MuiInputLabel-shrink': {
    transform: 'translate(14px, -6px) scale(0.75)',
  },
  '& .MuiInputBase-input': {
    '&:-webkit-autofill': {
      WebkitBoxShadow: theme.palette.mode === 'dark' 
        ? `0 0 0 30px ${theme.palette.background.paper} inset`
        : '0 0 0 30px white inset',
    },
  },
  '& .MuiInputAdornment-root': {
    marginLeft: 0,
  },
  '& .MuiIconButton-root': {
    padding: 8,
  },
}));

export const StyledButton = styled(Button).attrs(props => {
  // loading 속성을 제거하여 DOM에 전달되지 않도록 함
  const { loading, ...restProps } = props;
  return restProps;
})`
background: linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%);
border-radius: ${theme.customBorderRadius.large};
height: 56px;
font-size: 1rem;
font-weight: 600;
text-transform: none;
box-shadow: 0 3px 5px 2px rgba(33, 203, 243, .3);
transition: all 0.3s ease;
box-sizing: border-box;
width: 100%;

&:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 8px 2px rgba(33, 203, 243, .4);
}

@media (max-width: ${theme.breakpoints.values.sm}px) {
  height: 48px;
  font-size: 0.9rem;
}
`;

export const LanguageSelect = styled(Select).attrs(props => {
  const selectId = props.id || `select-${Math.random().toString(36).substring(7)}`;
  const labelId = `${selectId}-label`;

  return {
    id: selectId,
    labelId: labelId,
    'aria-labelledby': labelId
  };
})`
min-width: 120px;
height: 40px;
box-sizing: border-box;

.MuiSelect-select {
  padding: ${theme.customSpacing.small} ${theme.customSpacing.medium};
  box-sizing: border-box;
}
`;

export const FormContainer = styled.div`
display: flex;
flex-direction: column;
gap: ${theme.customSpacing.large};
max-width: 400px;
width: 100%;
margin: 0 auto;
padding: ${theme.customSpacing.xlarge} 0;
box-sizing: border-box;
`;

export const GradientText = styled.h1`
text-align: center;
font-weight: 700;
background: linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%);
-webkit-background-clip: text;
-webkit-text-fill-color: transparent;
margin-bottom: ${theme.customSpacing.xlarge};
box-sizing: border-box;
width: 100%;

@media (max-width: ${theme.breakpoints.values.sm}px) {
  font-size: 1.75rem;
}

@media (min-width: ${theme.breakpoints.values.sm}px) {
  font-size: 2rem;
}

@media (min-width: ${theme.breakpoints.values.md}px) {
  font-size: 2.5rem;
}
`;

export const InputWrapper = styled.div`
width: 100%;
margin-bottom: ${theme.customSpacing.medium};
box-sizing: border-box;
`;

export const LabelText = styled.label`
display: block;
margin-bottom: ${theme.customSpacing.small};
color: ${theme.palette.text.primary};
font-weight: 500;
box-sizing: border-box;
`;

export const StyledTablePaper = styled(Paper)`
width: 100%;
margin-bottom: ${theme.customSpacing.medium};
box-shadow: none;
border: 1px solid #e2e8f0;
border-radius: ${theme.customBorderRadius.medium};
overflow: hidden;
box-sizing: border-box;
`;

export const StyledTable = styled(Table)`
table-layout: fixed;
width: 100%;
box-sizing: border-box;
`;

// 공통 스크롤 가능한 테이블 컨테이너
export const ScrollableTableContainer = muiStyled(TableContainer)(({ theme }) => ({
  maxWidth: '100%',
  maxHeight: '70vh',
  overflowX: 'auto',
  overflowY: 'auto',
  position: 'relative',
  backgroundColor: theme.palette.mode === 'dark' 
    ? theme.palette.background.paper 
    : '#ffffff',
  border: theme.palette.mode === 'dark' 
    ? `1px solid ${theme.palette.divider}` 
    : 'none',
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.palette.mode === 'dark' 
    ? '0 1px 3px rgba(0,0,0,0.3)' 
    : '0 1px 3px rgba(0,0,0,0.05)',
  '&::-webkit-scrollbar': {
    width: '8px',
    height: '8px'
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: theme.palette.mode === 'dark' 
      ? theme.palette.grey[800] 
      : '#f1f1f1'
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.mode === 'dark' 
      ? theme.palette.grey[600] 
      : '#bbbbbb',
    borderRadius: '4px'
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: theme.palette.mode === 'dark' 
      ? theme.palette.grey[500] 
      : '#888888'
  }
}));

// 공통 테이블 셀 (개선된 버전)
export const StyledTableCell = muiStyled(TableCell)(({ theme, width }) => ({
  boxSizing: 'border-box',
  whiteSpace: 'nowrap',
  padding: '8px 16px',
  fontSize: '0.875rem',
  borderRight: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.mode === 'dark' 
    ? theme.palette.background.paper 
    : 'transparent',
  color: theme.palette.mode === 'dark' 
    ? theme.palette.text.primary 
    : 'inherit',
  '&:last-child': {
    borderRight: 'none'
  },
  '&.MuiTableCell-head': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontSize: 14,
    fontWeight: 600,
    textAlign: 'center',
    padding: '12px 8px',
    position: 'relative',
    borderRight: `1px solid ${theme.palette.primary.dark || theme.palette.primary.main}`,
    '&:last-child': {
      borderRight: 'none'
    },
    '& .MuiCheckbox-root': {
      color: theme.palette.common.white,
      '&.Mui-checked': {
        color: theme.palette.common.white
      }
    },
    ...(width && {
      width: width,
      minWidth: width,
      maxWidth: width
    })
  },
  '&.MuiTableCell-body': {
    fontSize: 14,
    padding: '8px',
    backgroundColor: 'transparent',
    borderBottom: theme.palette.mode === 'dark' 
      ? `1px solid ${theme.palette.divider}` 
      : '1px solid #cbd5e1',
    borderRight: theme.palette.mode === 'dark' 
      ? `1px solid ${theme.palette.divider}` 
      : '1px solid #cbd5e1',
    ...(width && {
      width: width,
      minWidth: width,
      maxWidth: width
    })
  },
  '& .MuiCheckbox-root': {
    color: theme.palette.primary.main,
    '&.Mui-checked': {
      color: theme.palette.primary.main
    }
  }
}));

// 공통 테이블 행 (개선된 버전)
export const StyledTableRow = muiStyled(TableRow)(({ theme }) => ({
  boxSizing: 'border-box',
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.mode === 'dark' 
      ? theme.palette.grey[800] 
      : theme.palette.action.hover || '#f8fafc',
  },
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' 
      ? `${theme.palette.grey[700]} !important`
      : (theme.palette.primary.light ? `${theme.palette.primary.light}20 !important` : '#f1f5f9 !important'),
  },
  '& td': {
    border: theme.palette.mode === 'dark' 
      ? `1px solid ${theme.palette.divider}` 
      : '1px solid #e2e8f0',
  },
}));

// 공통 필터 섹션
export const FilterSection = muiStyled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  width: '100%',
  position: 'relative'
}));

// 공통 날짜 필터 컨테이너
export const DateFilterContainer = muiStyled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  right: 0,
  zIndex: 10,
  display: 'flex',
  gap: theme.spacing(2),
  [theme.breakpoints.down('md')]: {
    position: 'static',
    marginBottom: theme.spacing(2)
  }
}));

// 공통 섹션 타이틀
export const SectionTitle = muiStyled(Typography)(({ theme }) => ({
  fontSize: '1.1rem',
  fontWeight: 600,
  marginBottom: theme.spacing(2),
  color: theme.palette.primary.main,
  display: 'flex',
  alignItems: 'center',
  '&::before': {
    content: '""',
    width: '4px',
    height: '24px',
    backgroundColor: theme.palette.primary.main,
    borderRadius: '4px',
    marginRight: theme.spacing(1.5)
  }
}));

// 공통 버튼 컨테이너
export const ButtonContainer = muiStyled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(2)
}));

// 공통 테이블 컨트롤 바
export const TableControlBar = muiStyled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(2)
}));

export const TableTextField = muiStyled(TextField)(({ theme }) => ({
  boxSizing: 'border-box',
  width: '100%',
  '& .MuiOutlinedInput-root': {
    height: 40,
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#ffffff',
    '& input': {
      padding: '8px 12px',
      fontSize: 14,
      boxSizing: 'border-box',
      color: theme.palette.mode === 'dark' ? theme.palette.text.primary : 'inherit',
    },
    '& fieldset': {
      borderColor: theme.palette.mode === 'dark' ? theme.palette.divider : '#94a3b8',
    },
    '&:hover fieldset': {
      borderColor: theme.palette.mode === 'dark' ? theme.palette.grey[600] : '#64748b',
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
    },
  },
}));

export const StyledContainer = muiStyled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  width: '100%',
  maxWidth: '1600px',
  margin: '0 auto',
  boxSizing: 'border-box',
  [theme.breakpoints.down('xl')]: {
    maxWidth: '100%',
  },
}));

export const StyledPaper = muiStyled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius || 12,
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#ffffff',
  boxShadow: theme.palette.mode === 'dark' 
    ? '0 2px 8px rgba(0, 0, 0, 0.3)' 
    : '0 2px 8px rgba(0, 0, 0, 0.08)',
  boxSizing: 'border-box',
  width: '100%',
}));

export const StyledDetailPaper = muiStyled(StyledPaper)(({ theme }) => ({
  marginTop: theme.spacing(3),
}));

export const OrderNoTypography = styled.div`
font-size: 14px;
color: ${theme.palette.text.secondary};
font-family: monospace;
box-sizing: border-box;
text-decoration: underline;
`;

export const languages = [
  { code: 'en', label: 'English' },
  { code: 'ko', label: '한국어' },
  { code: 'id', label: 'Bahasa Indonesia' }
];

export const MobileOrderCard = styled(Card)`
margin-bottom: ${theme.customSpacing.medium};
position: relative;
box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
border-radius: ${theme.customBorderRadius.medium};
overflow: visible;
box-sizing: border-box;

& .MuiCardContent-root {
  padding: ${theme.customSpacing.large} ${theme.customSpacing.medium} ${theme.customSpacing.medium};
  box-sizing: border-box;
}
`;

export const ItemNumberBadge = styled.div`
position: absolute;
top: ${theme.customSpacing.small};
right: ${theme.customSpacing.small};
display: flex;
align-items: center;
justify-content: center;
font-size: 0.75rem;
color: ${theme.palette.text.secondary};
box-sizing: border-box;
`;

export const MobileFieldLabel = styled(Typography)`
font-size: 0.75rem;
color: ${theme.palette.text.secondary};
margin-bottom: ${theme.customSpacing.xsmall};
display: block;
box-sizing: border-box;
`;

export const MobileActionButton = styled(Button)`
margin-top: ${theme.customSpacing.medium};
text-transform: none;
width: 100%;
box-sizing: border-box;
height: 48px;

&.MuiButton-outlined {
  border-color: ${theme.palette.primary.main};
  color: ${theme.palette.primary.main};
}

&.MuiButton-contained {
  background-color: ${theme.palette.primary.main};
  color: #ffffff;
}
`;

export const MobileOrderItemGrid = styled.div`
display: grid;
grid-template-columns: repeat(2, 1fr);
gap: ${theme.customSpacing.medium};
margin-bottom: ${theme.customSpacing.medium};
box-sizing: border-box;
`;

export const MobileFullWidthField = styled.div`
grid-column: span 2;
box-sizing: border-box;
`;

export const MobileItemActions = styled.div`
display: flex;
justify-content: space-between;
align-items: center;
width: 100%;
padding-top: ${theme.customSpacing.small};
border-top: 1px solid #e2e8f0;
margin-top: ${theme.customSpacing.medium};
box-sizing: border-box;
`;

export const MobileOrderSummary = styled.div`
margin-top: ${theme.customSpacing.large};
padding: ${theme.customSpacing.medium};
background-color: #f8fafc;
border-radius: ${theme.customBorderRadius.medium};
border: 1px solid #e2e8f0;
box-sizing: border-box;
`;

export const SummaryRow = styled.div`
display: flex;
justify-content: space-between;
align-items: center;
margin-bottom: ${theme.customSpacing.small};
box-sizing: border-box;

&:last-child {
  margin-bottom: 0;
  padding-top: ${theme.customSpacing.small};
  border-top: 1px solid #e2e8f0;
  font-weight: 600;
}
`;

export const MobileOrderNavigation = styled.div`
display: flex;
justify-content: space-between;
margin-top: ${theme.customSpacing.large};
box-sizing: border-box;
`;

export const BoundValueText = styled.span`
color: #1976d2;
font-weight: 600;
`;

export const MobileAddItemButton = styled(Button)`
width: 100%;
margin-top: ${theme.customSpacing.medium};
margin-bottom: ${theme.customSpacing.medium};
text-transform: none;
height: 48px;
border-style: dashed;

&.MuiButton-outlined {
  border-color: ${theme.palette.primary.main};
  color: ${theme.palette.primary.main};
}

&:hover {
  background-color: rgba(25, 118, 210, 0.04);
}
`;

export const MobileOrderHeader = styled.div`
display: flex;
flex-direction: column;
padding-bottom: ${theme.customSpacing.medium};
margin-bottom: ${theme.customSpacing.medium};
border-bottom: 1px solid #e2e8f0;

@media (min-width: ${theme.breakpoints.values.md}px) {
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
}
`;

export const MobileItemCardHeader = styled.div`
display: flex;
justify-content: space-between;
align-items: center;
width: 100%;
margin-bottom: ${theme.customSpacing.small};
`;

export const MobileDeleteIconWrapper = styled.div`
position: absolute;
top: 8px;
right: 8px;
z-index: 10;
`;

export const MobileItemIndex = styled.div`
background-color: #f1f5f9;
color: #475569;
font-size: 12px;
font-weight: 600;
padding: 4px 8px;
border-radius: 12px;
margin-right: ${theme.customSpacing.small};
`;

export const MobileBoundIndicator = styled.div`
background-color: rgba(25, 118, 210, 0.1);
color: #1976d2;
font-size: 11px;
font-weight: 600;
padding: 2px 6px;
border-radius: 8px;
display: inline-flex;
align-items: center;

& svg {
  font-size: 14px;
  margin-right: 2px;
}
`;

export const MobileItemContainer = styled.div`
max-height: 400px;
overflow-y: auto;
margin-bottom: ${theme.customSpacing.medium};
padding-right: ${theme.customSpacing.xsmall};

&::-webkit-scrollbar {
  width: 5px;
}

&::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 10px;
}

&::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 10px;
}

&::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
`;

export const EmptyItemPlaceholder = styled.div`
display: flex;
flex-direction: column;
align-items: center;
justify-content: center;
padding: ${theme.customSpacing.xlarge} ${theme.customSpacing.medium};
background-color: #f8fafc;
border: 1px dashed #cbd5e1;
border-radius: ${theme.customBorderRadius.medium};
margin-bottom: ${theme.customSpacing.medium};

& svg {
  color: #94a3b8;
  font-size: 48px;
  margin-bottom: ${theme.customSpacing.medium};
}

& p {
  color: #64748b;
  text-align: center;
}
`;